import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Stethoscope, Video, MapPin, Star, IndianRupee, Search, Info, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { useLocation as useGeo } from "@/contexts/LocationContext";
import LocationBar from "@/components/LocationBar";

const SPECS = ["", "General Physician", "Cardiologist", "Pediatrician", "Orthopedic Surgeon", "Gynecologist", "Neurologist", "Dermatologist", "ENT Specialist"];

export default function BookOPD() {
  const nav = useNavigate();
  const { loc } = useGeo();
  const [doctors, setDoctors] = useState([]);
  const [q, setQ] = useState("");
  const [spec, setSpec] = useState("");
  const [selected, setSelected] = useState(null);
  const [mode, setMode] = useState("video");
  const [slot, setSlot] = useState("");
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const p = {};
    if (spec) p.specialization = spec;
    if (q) p.q = q;
    if (loc) { p.near_lat = loc.lat; p.near_lng = loc.lng; }
    const { data } = await api.get("/doctors", { params: p });
    setDoctors(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [spec, loc?.lat, loc?.lng]);

  const confirm = async () => {
    if (!slot) { toast.error("Pick a slot time"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/appointments", {
        doctor_id: selected.id, mode, slot_time: new Date(slot).toISOString(), reason,
      });
      toast.success("Appointment confirmed");
      setSelected(null);
      if (data.mode === "video" && data.video_room) nav(`/video/${data.video_room}`);
      else nav("/patient");
    } catch (e) { toast.error(formatError(e)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-widest text-teal-700">Primary Care</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold mt-1">Book OPD consultation</h1>
          <p className="text-slate-600 text-sm mt-1">Choose a specialist for a video call or clinic visit.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/ai-triage"><Button variant="outline" data-testid="opd-ai-cta"><Sparkles className="w-4 h-4 mr-2" />Not sure? Ask MedHOne AI</Button></Link>
          <LocationBar />
        </div>
      </div>

      <Card className="p-4 rounded-2xl flex flex-wrap gap-3 items-end mb-6">
        <div className="flex-1 min-w-[220px]">
          <Label>Search doctor</Label>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="doctor-search" className="pl-9" placeholder="Name…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
          </div>
        </div>
        <div className="min-w-[200px]">
          <Label>Specialization</Label>
          <Select value={spec} onValueChange={setSpec}>
            <SelectTrigger data-testid="filter-spec"><SelectValue placeholder="All" /></SelectTrigger>
            <SelectContent>
              {SPECS.map((s) => <SelectItem key={s || "all"} value={s || "all"}>{s || "All specializations"}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="apply-filter" onClick={load} className="bg-slate-900 text-white">Apply</Button>
      </Card>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {doctors.filter(d => spec && spec !== "all" ? d.specialization === spec : true).map((d) => (
          <Card key={d.id} className="p-5 rounded-2xl card-lift" data-testid={`doctor-card-${d.id}`}>
            <div className="flex gap-3">
              {d.image ? (
                <img src={d.image} alt={d.name} data-testid={`doctor-photo-${d.id}`}
                  className="w-14 h-14 rounded-xl object-cover object-top border border-slate-200" />
              ) : (
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-teal-400 to-cyan-500 flex items-center justify-center text-white font-bold text-lg">
                  {d.name.split(" ").slice(-1)[0][0]}
                </div>
              )}
              <div className="flex-1">
                <Link to={`/facility/doctor/${d.id}`} className="font-display font-bold hover:text-teal-700 transition-colors" data-testid={`doctor-detail-${d.id}`}>{d.name}</Link>
                <div className="text-xs text-slate-500">{d.specialization}</div>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-600">
                  <span className="inline-flex items-center gap-1"><Star className="w-3 h-3 text-amber-500 fill-amber-500" />{d.rating}</span>
                  <span>{d.experience}y exp</span>
                  <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{d.address || d.city}</span>
                  {d.distance_km != null && <Badge variant="outline" className="text-[10px] py-0" data-testid={`dist-${d.id}`}>{d.distance_km} km</Badge>}
                </div>
              </div>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <div className="inline-flex items-center gap-1 font-mono text-lg font-bold text-slate-900">
                <IndianRupee className="w-4 h-4" />{d.fee}
              </div>
              <div className="flex gap-2">
                <Button data-testid={`book-video-${d.id}`} size="sm" variant="outline" onClick={() => { setSelected(d); setMode("video"); }}>
                  <Video className="w-4 h-4 mr-1" />Video
                </Button>
                <Button data-testid={`book-clinic-${d.id}`} size="sm" className="bg-teal-600 text-white" onClick={() => { setSelected(d); setMode("in_clinic"); }}>
                  <Stethoscope className="w-4 h-4 mr-1" />Clinic
                </Button>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Book with {selected?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Mode</Label>
              <div className="flex gap-2 mt-1">
                <Badge className={mode === "video" ? "bg-teal-600 cursor-pointer" : "bg-slate-200 text-slate-800 cursor-pointer"} onClick={() => setMode("video")} data-testid="mode-video">Video</Badge>
                <Badge className={mode === "in_clinic" ? "bg-teal-600 cursor-pointer" : "bg-slate-200 text-slate-800 cursor-pointer"} onClick={() => setMode("in_clinic")} data-testid="mode-clinic">In-clinic</Badge>
              </div>
            </div>
            <div>
              <Label>Slot date & time</Label>
              <Input data-testid="slot-time" type="datetime-local" value={slot} onChange={(e) => setSlot(e.target.value)} />
            </div>
            <div>
              <Label>Reason (optional)</Label>
              <Textarea data-testid="reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Symptoms…" />
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="confirm-booking" disabled={loading} onClick={confirm} className="bg-teal-600 text-white">
              {loading ? "Confirming…" : `Confirm • ₹${selected?.fee}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
